/**
 * React Query hooks for Retail > Fleet Map.
 *
 * - useRetailWeeks()           GET /v1/retail/weeks
 * - useRetailStores(filters)   GET /v1/retail/stores?week_ending=...
 * - useRetailStoreDetail(no)   GET /v1/retail/stores/{store_no}
 *
 * RBAC: Fleet Map is an Everyone tool, gated only on core:read (every
 * signed-in employee). The API returns 403 for a caller who somehow lacks
 * even that; callers should check the error status and render an
 * AccessDenied state rather than crashing.
 *
 * The API may not be live when this page first ships to dev. Every hook
 * returns a stable empty-data shape on fetch failure so the page renders
 * a clean "no data yet" state instead of crashing.
 */

import { useQuery } from '@tanstack/react-query';
import { formatBusinessDate, formatInstant } from '@ampersandbrands/ui';
import { apiGet } from './api';
import { API_BASE } from './apiBase';

export const RETAIL_API_BASE = `${API_BASE}/v1/retail`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SeverityTier = 'critical' | 'pressure' | 'watch' | 'stable' | 'no_baseline';
export type SalesTier = '200k+' | '150k-200k' | '100k-150k' | 'under_100k';

export type RetailSalesWindow = {
  l7_ty: number | null;
  l7_ly: number | null;
  l7_comp: number | null;
  l4_ty: number | null;
  l4_ly: number | null;
  l4_comp: number | null;
  l13_ty: number | null;
  l13_ly: number | null;
  l13_comp: number | null;
  ytd_ty: number | null;
  ytd_ly: number | null;
  ytd_comp: number | null;
};

/**
 * A single store row from GET /v1/retail/stores.
 *
 * The server returns `pilot16` / `active` as JSON booleans (it maps the DB
 * tinyint to a boolean). Older callers compared `=== 1`; the isPilot/isActive
 * helpers below treat both 1 and true as truthy.
 *
 * `sales` and `avg_trx` are NULL when there is no weekly metrics row for the
 * selected week (a store that opened after that week, or a gap in the feed).
 * Always read these through optional chaining; never assume they exist.
 *
 * The memo_* / scorecard_true_comp / has_fy25_baseline / landlord_source_note
 * fields are added by a parallel api change. They are optional here so the
 * page builds and runs before that api change deploys; render '--' when absent.
 *
 * `lat` and `lng` are typed as `number` here (post-coercion). The API may
 * return them as JSON strings (MySQL DECIMAL serialized as text). The hooks
 * `useRetailStores` and `useRetailStoreDetail` coerce both fields with
 * `Number(...)` before returning data, so all consumers receive real numbers.
 * Use `Number.isFinite(s.lat)` to filter stores with genuinely missing coords.
 */
export type RetailStoreRow = {
  store_no: string;
  store_name: string;
  center: string | null;
  city: string;
  state: string;
  region: string | null;
  district: string | null;
  alpha: string | null;
  lat: number;
  lng: number;
  landlord_group: string | null;
  landlord_detail: string | null;
  pilot16: number | boolean;
  pilot_rank: number | null;
  active: number | boolean;
  sales: RetailSalesWindow | null;
  avg_trx: RetailSalesWindow | null;
  severity: SeverityTier;
  sales_tier: SalesTier;
  // Optional fields from the parallel api change (treat as nullable).
  landlord_source_note?: string | null;
  scorecard_true_comp?: boolean | null;
  has_fy25_baseline?: boolean | null;
  memo_2025_sales?: number | null;
  memo_pct_of_total?: number | null;
  memo_ytd_comp?: number | null;
};

export type RetailStoresResponse = {
  week_ending: string;
  computed_at: string;
  stores: RetailStoreRow[];
};

export type RetailMonthlyRow = {
  fiscal_month_start: string;
  /**
   * Which fiscal year this period belongs to, supplied by the API from the
   * fiscal calendar. Optional so older cached responses still parse; when it is
   * absent everywhere, splitMonthlyByFy reports no current-fiscal-year rows
   * rather than guessing a fiscal-year start from the date.
   */
  fiscal_year?: number | null;
  sales_ty: number | null;
  sales_ly: number | null;
  comp: number | null;
  plan: number | null;
  vs_plan: number | null;
};

/**
 * Shape of a single weekly history entry returned by GET /v1/retail/stores/{store_no}.
 * The weekly array is ordered newest-first by the API.
 */
export type RetailWeeklyHistoryRow = {
  week_ending: string;
  computed_at: string | null;
  sales: RetailSalesWindow | null;
  avg_trx: RetailSalesWindow | null;
  severity: SeverityTier;
  sales_tier: SalesTier;
};

/**
 * The `store` object on the detail response carries master fields only.
 * Sales / avg_trx / severity / sales_tier live on the `weekly` array, NOT on
 * the store object (this is the contract that caused the page to blank when
 * the panel read store.sales). Resolve metrics from the weekly row matching
 * the selected week via resolveWeeklyRow, falling back to weekly[0] (newest).
 */
export type RetailStoreDetailStore = Omit<
  RetailStoreRow,
  'sales' | 'avg_trx' | 'severity' | 'sales_tier'
> & {
  sales?: RetailSalesWindow | null;
  avg_trx?: RetailSalesWindow | null;
  severity?: SeverityTier;
  sales_tier?: SalesTier;
};

/**
 * Shape returned by GET /v1/retail/stores/{store_no}.
 * The API wraps the result in { store, weekly, monthly } — NOT a flat object.
 */
export type RetailStoreDetailResponse = {
  store: RetailStoreDetailStore;
  weekly: RetailWeeklyHistoryRow[];
  monthly: RetailMonthlyRow[];
};

export type RetailWeeksResponse = {
  weeks: string[];
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useRetailWeeks() {
  const query = useQuery<RetailWeeksResponse, Error>({
    queryKey: ['retail-weeks'],
    queryFn: () => apiGet<RetailWeeksResponse>(`${RETAIL_API_BASE}/weeks`),
    staleTime: 10 * 60_000,
    retry: false,
  });
  return {
    weeks: query.data?.weeks ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
}

export type RetailStoresFilters = {
  week_ending?: string;
};

/** Coerce a store row's lat/lng to real numbers regardless of API serialization.
 * MySQL DECIMAL columns may arrive as JSON strings ("36.067") rather than
 * numbers. Number("36.067") === 36.067; Number("") and Number(null) become 0;
 * Number(undefined) becomes NaN — all safe for a subsequent Number.isFinite()
 * guard in the marker filter.
 *
 * Exported for unit testing only. Prefer using the hooks which apply this
 * automatically — do not call this manually in application code.
 */
export function coerceLatLng<T extends { lat: unknown; lng: unknown }>(store: T): T & { lat: number; lng: number } {
  return { ...store, lat: Number(store.lat), lng: Number(store.lng) };
}

export function useRetailStores(filters: RetailStoresFilters = {}) {
  const qs = filters.week_ending ? `?week_ending=${encodeURIComponent(filters.week_ending)}` : '';
  const query = useQuery<RetailStoresResponse, Error>({
    queryKey: ['retail-stores', filters.week_ending ?? ''],
    queryFn: () => apiGet<RetailStoresResponse>(`${RETAIL_API_BASE}/stores${qs}`),
    staleTime: 5 * 60_000,
    retry: false,
  });
  return {
    weekEnding: query.data?.week_ending ?? null,
    computedAt: query.data?.computed_at ?? null,
    stores: (query.data?.stores ?? []).map(coerceLatLng),
    isLoading: query.isLoading,
    error: query.error,
  };
}

export function useRetailStoreDetail(storeNo: string | null | undefined) {
  const query = useQuery<RetailStoreDetailResponse, Error>({
    queryKey: ['retail-store-detail', storeNo],
    queryFn: () =>
      apiGet<RetailStoreDetailResponse>(
        `${RETAIL_API_BASE}/stores/${encodeURIComponent(storeNo ?? '')}`,
      ),
    enabled: !!storeNo,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const rawStore = query.data?.store ?? null;
  return {
    store: rawStore ? coerceLatLng(rawStore) : null,
    weekly: query.data?.weekly ?? [],
    monthly: query.data?.monthly ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
}

/**
 * Triggers a browser download of the fleet scorecard Excel export.
 * Calls GET /v1/retail/export?week_ending=YYYY-MM-DD and saves the blob.
 * Mirrors the downloadOrdersXlsx pattern from useOrders.ts.
 */
export async function downloadRetailXlsx(weekEnding: string | null): Promise<void> {
  const qs = weekEnding ? `?week_ending=${encodeURIComponent(weekEnding)}` : '';
  const url = `${RETAIL_API_BASE}/export${qs}`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Export failed: ${res.status} ${text}`);
  }
  const blob = await res.blob();
  const dispo = res.headers.get('content-disposition') ?? '';
  const m = dispo.match(/filename="([^"]+)"/);
  const filename = m?.[1] ?? `retail-fleet-${weekEnding ?? 'export'}.xlsx`;
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

/**
 * Returns Tailwind background and text classes for a severity tier.
 * Uses the existing Tailwind palette: red=critical, orange=pressure,
 * amber=watch, green=stable, gray=no_baseline.
 */
export function severityClasses(s: SeverityTier): string {
  switch (s) {
    case 'critical':    return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
    case 'pressure':    return 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300';
    case 'watch':       return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300';
    case 'stable':      return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
    case 'no_baseline': return 'bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-300';
  }
}

/**
 * Hex ring color for a severity tier, from Franco's original scorecard map.
 * critical -> deep red, pressure -> burnt orange, watch -> gold,
 * stable (near-flat / positive) -> teal, no_baseline -> gray.
 */
export function severityColor(s: SeverityTier): string {
  switch (s) {
    case 'critical':    return '#A43C2A';
    case 'pressure':    return '#B86B2B';
    case 'watch':       return '#8A7A45';
    case 'stable':      return '#2F7E7C';
    case 'no_baseline': return '#6D6D6D';
  }
}

/** Most-severe-first ordering, mirrors the api deriveSeverity thresholds. */
export const SEVERITY_ORDER: SeverityTier[] = [
  'critical',
  'pressure',
  'watch',
  'stable',
  'no_baseline',
];

// ---------------------------------------------------------------------------
// Landlord group color map (from Franco's landlordColors)
// ---------------------------------------------------------------------------

export const LANDLORD_COLORS: Record<string, string> = {
  Brookfield: '#8A7A45',
  'Simon / Taubman': '#A43C2A',
  'Westfield / URW': '#6F4A7E',
  Macerich: '#2F7E7C',
  CBL: '#6D6D6D',
  MetroNational: '#253A5A',
  'Independent / Other': '#9C7A3A',
  'Company-Owned / Non-Mall': '#111111',
  Unknown: '#999999',
};

/** Fill color for a landlord group; falls back to the Unknown gray. */
export function landlordColor(group: string | null | undefined): string {
  const fallback = '#999999'; // Unknown gray
  if (!group) return fallback;
  return LANDLORD_COLORS[group] ?? fallback;
}

/** Gold used for the Pilot 16 halo + accents (Franco's --amp-gold-bright). */
export const PILOT_GOLD = '#D4AF37';

/**
 * Donut marker radius in px. radius = 6 + sqrt(max(0, ytd) / maxYtd) * 9.
 * `maxYtd` is the largest YTD FY26 sales across the loaded fleet (so the
 * biggest store maps to radius 15, the smallest toward 6).
 */
export function markerRadius(ytdTy: number | null | undefined, maxYtd: number): number {
  const ytd = ytdTy ?? 0;
  const max = maxYtd > 0 ? maxYtd : 1;
  return 6 + Math.sqrt(Math.max(0, ytd) / max) * 9;
}

/** Largest YTD FY26 sales across the fleet; used as the marker-radius denominator. */
export function maxYtdSales(stores: RetailStoreRow[]): number {
  let max = 0;
  for (const s of stores) {
    const v = s.sales?.ytd_ty ?? 0;
    if (v > max) max = v;
  }
  return max;
}

/**
 * Returns the Leaflet CircleMarker radius (px) for a sales tier. Retained for
 * any caller that wants tier-bucketed sizing; the map itself uses markerRadius.
 */
export function salesTierRadius(t: SalesTier): number {
  switch (t) {
    case '200k+':       return 12;
    case '150k-200k':   return 10;
    case '100k-150k':   return 8;
    case 'under_100k':  return 6;
  }
}

/**
 * Formats a severity tier for display, matching Franco's severity bucket
 * labels (the ranges he wrote on the legend).
 */
export function severityLabel(s: SeverityTier): string {
  switch (s) {
    case 'critical':    return 'Critical (<= -30%)';
    case 'pressure':    return 'Pressure (-20% to -30%)';
    case 'watch':       return 'Watch (-10% to -20%)';
    case 'stable':      return 'Near flat / positive';
    case 'no_baseline': return 'New / no FY25';
  }
}

/**
 * Formats a decimal comp ratio as "+12.3%" or "-5.1%".
 * Zero renders as "+0.0%" (a real value), null/undefined/NaN as "--".
 */
export function formatComp(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '--';
  const pct = v * 100;
  const sign = pct >= 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}%`;
}

/** Alias for comp formatting on non-comp percentage values (memo %, vs plan). */
export const formatPct = formatComp;

/**
 * Formats a dollar amount. Large values collapse to "$7.14M" like Franco's
 * fmtMoney; smaller values render as "$12,345". Zero is "$0", null "--".
 */
export function formatDollars(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '--';
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(v);
}

/** Formats an avg $/transaction value as "$22.45". Zero is "$0.00", null "--". */
export function formatDpt(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '--';
  return `$${v.toFixed(2)}`;
}

/** Formats a date string "2026-06-06" as "6/6/2026". */
export function formatWeekDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return `${m}/${d}/${y}`;
}

/**
 * Formats an ISO timestamp as a human time in the viewer's timezone, e.g.
 * "Jun 12, 2026, 6:05 AM". Delegates to the shared `formatInstant`, so a
 * timestamp the API sent without a zone marker is read as UTC.
 */
export function formatComputedAt(iso: string): string {
  return formatInstant(iso) || iso;
}

/** True when a store is one of the Pilot 16. Handles both numeric (1/0) and boolean API shapes. */
export function isPilot(store: Pick<RetailStoreRow, 'pilot16'>): boolean {
  return store.pilot16 === 1 || store.pilot16 === true;
}

/** True when a store is active. Handles both numeric (1/0) and boolean API shapes. */
export function isActive(store: Pick<RetailStoreRow, 'active'>): boolean {
  return store.active === 1 || store.active === true;
}

// ---------------------------------------------------------------------------
// Stat-box math (replicates Franco's KPI + visible-KPI boxes)
// ---------------------------------------------------------------------------

export type FleetStats = {
  /** Number of stores in the set. */
  count: number;
  /** Sum of YTD FY26 sales across the set. */
  ytdSales: number;
  /** Number of Pilot 16 stores in the set. */
  pilotCount: number;
  /**
   * Median YTD comp across stores that have a value (null comps excluded).
   * null when no store in the set has a comp.
   */
  medianYtdComp: number | null;
};

/** Median of a numeric array. Returns null for an empty array. */
export function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[mid] ?? null;
  const lo = sorted[mid - 1];
  const hi = sorted[mid];
  if (lo == null || hi == null) return null;
  return (lo + hi) / 2;
}

/**
 * Computes the stat boxes for a set of stores. Mirrors Franco's
 * updateVisibleKpis: sum of YTD FY26, pilot count, and the median of the
 * non-null YTD comps.
 */
export function computeFleetStats(stores: RetailStoreRow[]): FleetStats {
  let ytdSales = 0;
  let pilotCount = 0;
  const comps: number[] = [];
  for (const s of stores) {
    ytdSales += s.sales?.ytd_ty ?? 0;
    if (isPilot(s)) pilotCount += 1;
    const c = s.sales?.ytd_comp;
    if (c != null && !Number.isNaN(c)) comps.push(c);
  }
  return {
    count: stores.length,
    ytdSales,
    pilotCount,
    medianYtdComp: median(comps),
  };
}

// ---------------------------------------------------------------------------
// Weekly-row resolution and fiscal-period helpers
// ---------------------------------------------------------------------------

/**
 * Returns the weekly history row that matches selectedWeekEnding, or weekly[0]
 * as a fallback when no match is found. Returns null when the array is empty.
 *
 * The panel reads Net Sales and Avg $/TRX from this row rather than from
 * store.sales because the store object carries the snapshot for the
 * week_ending used on the list query, which may differ from the week the
 * user has focused in the detail panel.
 */
export function resolveWeeklyRow(
  weekly: RetailWeeklyHistoryRow[],
  selectedWeekEnding: string | null | undefined,
): RetailWeeklyHistoryRow | null {
  if (weekly.length === 0) return null;
  if (selectedWeekEnding) {
    const match = weekly.find((r) => r.week_ending === selectedWeekEnding);
    if (match) return match;
  }
  return weekly[0] ?? null;
}

/**
 * The latest fiscal year present in the rows, or null when none carry one.
 *
 * The fiscal year is read off the row, not computed from its date. The Lolli &
 * Pops fiscal year ends on the Saturday nearest Jan 31, so its start moves year
 * to year: FY2025 opened 2025-02-02, FY2026 opened 2026-02-01, FY2027 opens
 * 2027-01-31, FY2028 opens 2028-01-30. The previous version of this function
 * assumed a fixed "YYYY-02-01" turn, which put FY2027's first period in the
 * prior-year bucket and was already a day off for FY2025. The API now supplies
 * fiscal_year from the fiscal calendar, so there is one source of truth for it.
 */
export function latestFiscalYear(monthly: RetailMonthlyRow[]): number | null {
  let latest: number | null = null;
  for (const m of monthly) {
    const fy = m.fiscal_year;
    if (typeof fy === 'number' && (latest === null || fy > latest)) latest = fy;
  }
  return latest;
}

/**
 * Splits monthly rows into current-FY and prior-FY buckets.
 * Current FY = rows whose fiscal_year matches the latest fiscal year present.
 *
 * Rows with no fiscal_year fall into priorFy: they sit outside the fiscal
 * calendar mirror, which only happens for history older than the mirror covers.
 */
export function splitMonthlyByFy(monthly: RetailMonthlyRow[]): {
  currentFy: RetailMonthlyRow[];
  priorFy: RetailMonthlyRow[];
} {
  const currentFyYear = latestFiscalYear(monthly);
  if (currentFyYear === null) return { currentFy: [], priorFy: [] };
  const currentFy = monthly.filter((m) => m.fiscal_year === currentFyYear);
  const priorFy = monthly.filter((m) => m.fiscal_year !== currentFyYear);
  return { currentFy, priorFy };
}

/**
 * Returns a short label for a fiscal month period.
 * Appends the start day when two sibling periods share the same calendar month
 * and year (e.g. 4-4-5 calendars can have two "Aug" periods in the same FY).
 *
 * @param iso      ISO date string of the period start ("YYYY-MM-DD")
 * @param siblings All ISO date strings in the same display group (used for
 *                 collision detection; may include iso itself)
 */
export function formatFiscalMonth(iso: string, siblings: string[]): string {
  // A fiscal period start is a business date: the shared helper renders it
  // from the digits ("Aug 2, 2026") instead of constructing a Date, so the
  // label can't slide into the neighbouring month in a non-UTC browser.
  const formatted = formatBusinessDate(iso);
  if (!formatted) return iso;
  const [monthName = '', dayWithComma = ''] = formatted.split(' ');
  const day = dayWithComma.replace(',', '');
  const yearMonth = iso.slice(0, 7); // YYYY-MM
  const collision = siblings.some((s) => s !== iso && s.slice(0, 7) === yearMonth);
  return collision ? `${monthName} ${day}` : monthName;
}

// ---------------------------------------------------------------------------
// RBAC
// ---------------------------------------------------------------------------

/**
 * Fleet Map is an Everyone tool (spec 00_access-binary-grants.md, 2026-08-09):
 * every signed-in employee sees it, gated only on the core:read session
 * baseline (shared/catalog.ts 'fleet-map' capability). Renamed from the old
 * retail.scorecard.read capability, which is never granted anymore.
 */
export function hasRetailScorecard(perms: string[] | undefined): boolean {
  if (!perms || perms.length === 0) return false;
  return perms.includes('core:read');
}
